import React from 'react';
import { GoogleLogo } from '@phosphor-icons/react';
import { useFormatMessage } from '../../../i18n-review/client/Msg';
import { useAuthOptional } from '../../../contexts/AuthContext';
import { trackEvent } from '../../../utils/analytics/analytics';
import { useMenuPulse } from '../menu/useMenuPulse';
import { formatNavFigure } from './format-nav-figure';
import { navIndexMessages } from './nav-index-messages';
import { useValueSheetLedger } from './useValueSheetLedger';
import type { PrimaryNavLocale } from '../primary-nav-links';

export interface ValueSheetProps {
  locale: PrimaryNavLocale;
  onRequestClose: () => void;
}

/**
 * Guest popover under the account pill (TEC navbar redesign Phase 1). Two variants picked by
 * whether the visitor has local history: "free" pitches the product, "keep" leans on loss
 * aversion for a visitor who already has recent searches or a viewed job on this device.
 * Guest-side saved jobs don't exist yet, so the "keep" variant only ever lists searches/viewed,
 * never saved jobs, matching the spec's explicit "do not build that in this phase".
 */
export const ValueSheet: React.FC<ValueSheetProps> = ({ locale, onRequestClose }) => {
  const formatMessage = useFormatMessage();
  const auth = useAuthOptional();
  const ledger = useValueSheetLedger(locale);
  // Fetched on open only (menu-pulse is otherwise fetched by the hamburger menu once it's been
  // opened); shares the same 10-minute cache, so opening both costs one request, not two.
  const pulse = useMenuPulse(locale, true);

  React.useEffect(() => {
    trackEvent({ name: 'auth_interaction', params: { action: 'value_sheet_open', surface: 'navbar', variant: ledger.variant, locale } });
    // Fire once per mount (the sheet is unmounted on close), not on every ledger recompute.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignIn = (surface: 'google' | 'email') => {
    trackEvent({ name: 'auth_interaction', params: { action: 'sign_in_click', surface: 'navbar_sheet', locale, method: surface } });
    auth?.signIn();
    onRequestClose();
  };

  const isKeep = ledger.variant === 'keep';
  const newTodayFigure = pulse ? formatNavFigure(pulse.newToday, locale) : null;

  return (
    <div
      role="dialog"
      aria-label={formatMessage(isKeep ? navIndexMessages.sheetKeep : navIndexMessages.sheetFree)}
      className="absolute right-0 top-[46px] w-[312px] rounded-2xl border border-jc-border bg-jc-surface p-3.5 pb-3 shadow-[0_18px_40px_-18px_rgba(22,21,15,0.4)] z-20"
    >
      <div className="flex items-center gap-2.5 mb-1">
        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-jc-muted whitespace-nowrap">
          {formatMessage(isKeep ? navIndexMessages.sheetKeep : navIndexMessages.sheetFree)}
        </span>
        <span className="h-px flex-1 bg-jc-border" aria-hidden />
      </div>

      {isKeep ? (
        <div className="flex flex-col">
          {ledger.recentSearches.length > 0 ? (
            <div className="flex justify-between items-baseline gap-3 py-[7px] border-b border-jc-border">
              <b className="text-[13px] font-semibold text-jc-ink whitespace-nowrap">
                {formatMessage(navIndexMessages.sheetSearches, { n: ledger.recentSearches.length })}
              </b>
              <span className="text-xs text-jc-muted text-right truncate max-w-[160px]">
                {ledger.recentSearches.map((chip) => chip.label).join(', ')}
              </span>
            </div>
          ) : null}
          {ledger.lastViewedTitle ? (
            <div className="flex justify-between items-baseline gap-3 py-[7px]">
              <b className="text-[13px] font-semibold text-jc-ink whitespace-nowrap">{formatMessage(navIndexMessages.sheetViewed)}</b>
              <span className="text-xs text-jc-muted text-right truncate max-w-[160px]">{ledger.lastViewedTitle}</span>
            </div>
          ) : null}
          <p className="text-[11px] text-jc-muted mt-1.5 mb-0">{formatMessage(navIndexMessages.sheetDevice)}</p>
        </div>
      ) : (
        <div className="flex flex-col">
          <div className="flex justify-between items-baseline gap-3 py-[7px] border-b border-jc-border">
            <b className="text-[13px] font-semibold text-jc-ink whitespace-nowrap">{formatMessage(navIndexMessages.sheetTrack)}</b>
            <span className="text-xs text-jc-muted text-right">{formatMessage(navIndexMessages.sheetTrackNote)}</span>
          </div>
          <div className="flex justify-between items-baseline gap-3 py-[7px] border-b border-jc-border">
            <b className="text-[13px] font-semibold text-jc-ink whitespace-nowrap">{formatMessage(navIndexMessages.sheetAlerts)}</b>
            <span className="text-xs text-jc-muted text-right">
              {newTodayFigure
                ? formatMessage(navIndexMessages.sheetAlertsNote, {
                    n: newTodayFigure,
                    // Phase 1 review fix F4: renders the live figure as an <em> with its own
                    // styling instead of plain text, per the product owner's request.
                    em: ((chunks: React.ReactNode) => (
                      <em className="not-italic font-display font-semibold text-jc-accent tabular-nums">{chunks}</em>
                    )) as unknown as string,
                  })
                : formatMessage(navIndexMessages.sheetAlertsNoteLoading)}
            </span>
          </div>
          <div className="flex justify-between items-baseline gap-3 py-[7px]">
            <b className="text-[13px] font-semibold text-jc-ink whitespace-nowrap">{formatMessage(navIndexMessages.sheetKit)}</b>
            <span className="text-xs text-jc-muted text-right">{formatMessage(navIndexMessages.sheetKitNote)}</span>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => handleSignIn('google')}
        className="flex items-center justify-center gap-2 w-full h-[38px] mt-2.5 rounded-full bg-jc-accent text-jc-surface text-[13px] font-bold cursor-pointer hover:opacity-90 transition-opacity"
      >
        <GoogleLogo weight="bold" className="w-4 h-4" aria-hidden />
        {formatMessage(isKeep ? navIndexMessages.sheetKeepGoogle : navIndexMessages.sheetGoogle)}
      </button>
      <button
        type="button"
        onClick={() => handleSignIn('email')}
        className="block w-full text-center text-xs text-jc-ink-secondary underline underline-offset-[3px] mt-2 cursor-pointer"
      >
        {formatMessage(navIndexMessages.sheetEmail)}
      </button>
      {!isKeep ? <p className="text-[11px] text-jc-muted text-center mt-2 mb-0">{formatMessage(navIndexMessages.sheetFine)}</p> : null}
    </div>
  );
};
